/* eslint-disable no-secrets/no-secrets */
import React from "react";
import lodash from "lodash";
import moment from "moment";
import FileSaver from "file-saver";
import { camelize } from "humps";
import { converge, compose, map, zipObj, toPairs, pluck, curry, assoc, reduce, keys } from "ramda";
import { Loader } from "cxa-components";
import { branch, renderComponent } from "recompose";
import accounting from "accounting";
import { CLIENT_CONFIG } from "../components/constants.js";
import benefitsErrorHandler from "./benefitsErrorHandler";
import { isExist } from "./is";
/**
 * Returns a HOC that renders a Loader in place of the wrapped Component if the predicates isLoading resolves to true
 * @param {function} isLoading - A function to determine if the component should be on loading state
 * @return {function} a HOC wrapper
 */
export const loaderWhileLoading = isLoading =>
  branch(
    isLoading,
    renderComponent(Loader) // `Spinner` is a React component
  );

/**
 * Converts an object of key value to an array of objects of id and value
 * @param {object} obj - Object of key value, e.g. { field1: "Some Value", field2: "Some Value" }
 * @return {array} output - Array of objects of id and value, e.g. [{ id: "field1", value: "Some Value" }, { id: "field2", value: "Some Value" }]
 * @see fromArrayOfIdAndValue
 */
export const toArrayOfIdAndValue = compose(map(zipObj(["id", "value"])), toPairs);

/**
 * Converts an array of objects of id and value to an object of key value
 * @param {array} obj - Array of objects of id and value, e.g. [{ id: "field1", value: "Some Value" }, { id: "field2", value: "Some Value" }]
 * @return {object} output - Object of key value, e.g. { field1: "Some Value", field2: "Some Value" }
 * @see toArrayOfIdAndValue
 */
export const fromArrayOfIdAndValue = converge(zipObj, [pluck("id"), pluck("value")]);

/**
 * Renames keys in a given object based on key-to-new-key map provided
 * @param {object} keysMap - Map of key-to-new-key renaming
 * @param {object} obj - Object with keys to be renamed
 * @return {object} output - A copy of obj with the keys renamed
 */
export const renameKeys = curry((keysMap, obj) =>
  reduce((acc, key) => assoc(keysMap[key] || key, obj[key], acc), {}, keys(obj))
);

/**
 * Recursively parses number and boolean strings in a given value parameter to number and boolean respectively
 * @param {any} value - Value to be converted, can be an array, object, or primitives
 * @param {object} options - Options for parsing, supports camelizeKeys
 * @return {object} output - A deep copy of array/object or primitives with converted value(s)
 */
export const parseData = (value, options) => {
  const { camelizeKeys } = {
    camelizeKeys: false,
    ...(options || {}),
  };

  if (value === null || typeof value === "undefined") {
    return value;
  } else if (Array.isArray(value)) {
    return value.map(item => parseData(item, { camelizeKeys }));
  } else if (typeof value === "object") {
    return Object.entries(value).reduce((obj, [key, value]) => {
      const newKey = camelizeKeys ? camelize(key) : key;
      return {
        ...obj,
        [newKey]: parseData(value, { camelizeKeys }),
      };
    }, {});
  } else if ((typeof value === "string" || value instanceof String) && value.length > 0) {
    switch (value.toLowerCase().trim()) {
      case "true":
        /* case "yes": */ return true;

      case "false":
      /* case "no": */ case null:
        return false;

      default: {
        if (!isNaN(value)) {
          return (value.match(/\./g) || []).length > 0
            ? Number.parseFloat(value)
            : Number.parseInt(value);
        }
        return value;
      }
    }
  } else {
    return value;
  }
};

export const arrayToMapById = (array, options) => {
  const { idKey, includeAll, camelizeKeys } = {
    idKey: "id",
    includeAll: false,
    camelizeKeys: false,
    ...(options || {}),
  };

  const byId = array.reduce((obj, item) => {
    const key = item[idKey];
    const finalKey = camelizeKeys ? camelize(key) : key;
    return {
      ...obj,
      [finalKey]: item,
    };
  }, {});

  const all =
    includeAll &&
    array.map(item => {
      const key = item[idKey];
      return camelizeKeys ? camelize(key) : key;
    });

  return {
    byId,
    all: includeAll ? all : undefined,
  };
};

export const filterNull = item => item;

export const arrayToObject = x =>
  lodash.isArray(x)
    ? x.reduce((acc, i) => {
        acc[i] = true;
        return acc;
      }, {})
    : {};

export const linkLogicRelationships = {
  ByPlanValue: 1,
  ByEqualPlanValue: 2,
  ByGroupValue: 3,
  ByPlanAndGroupValue: 4,
  By1To1MatchPlanID: 5,
  BySumAssuredAmount: 6, //x
  BySumAssuredAmountGreater: 7, //x
  BySameGroupValueAndLessEqualPlanValue: 8,
  BySameGroupValueOrNoCoverage: 9,
  By1To1MatchPlanGroupValue: 10, //x
  By1To1MatchPlanIdOrNoCoverage: 11, // fake - to make link logic work
  BySameGroupValueOrNoCoverageAndLessEqualPlanValue: 12,
  properties: {
    1: { name: "ByPlanValue" },
    2: { name: "ByEqualPlanValue" },
    3: { name: "ByGroupValue" },
    4: { name: "ByPlanAndGroupValue" },
    5: { name: "By1To1MatchPlanID" },
    6: { name: "BySumAssuredAmount" },
    7: { name: "BySumAssuredAmountGreater" },
    8: { name: "BySameGroupValueAndLessEqualPlanValue" },
    9: { name: "BySameGroupValueOrNoCoverage" },
    10: { name: "By1To1MatchPlanGroupValue" },
    11: { name: "By1To1MatchPlanIdOrNoCoverage" },
    12: { name: "BySameGroupValueOrNoCoverageAndLessEqualPlanValue" },
  },
};

// TODO Once API is returning the fields in camelcase, remove this function.
export const toCamelImproved = o => {
  let build;
  let key;
  let destKey;
  let value;
  let hasAtleastOneOwnProperty;

  if (o instanceof Array) {
    build = [];
    for (key in o) {
      if (o.hasOwnProperty(key)) {
        value = o[key];

        if (typeof value === "object") {
          value = toCamelImproved(value);
        } else if (value !== null && typeof value === "string") {
          value = value.trim();
        }
        build.push(value);
      }
    }
  } else {
    build = {};
    hasAtleastOneOwnProperty = false;

    for (key in o) {
      if (o.hasOwnProperty(key)) {
        hasAtleastOneOwnProperty = true;
        destKey = lodash.camelCase(key); //(key.charAt(0).toLowerCase() + key.slice(1) || key).toString();
        value = o[key];
        if (value !== null && typeof value === "object") {
          value = toCamelImproved(value);
        } else if (value !== null && typeof value === "string") {
          value = value.trim();
        }

        build[destKey] = value;
      }
    }
    if (!hasAtleastOneOwnProperty) {
      build = o;
    }
  }

  return build;
};

export const retrieveQueryParams = lowerCasekeys => {
  const query = location.search.replace(/(^\?)/, "");
  const params = {};
  if (query.length > 0) {
    query.split("&").forEach(item => {
      const kv = item.split("=");
      if (lowerCasekeys) {
        params[kv[0].toLowerCase()] = kv[1];
      } else {
        params[kv[0]] = kv[1];
      }
    });
  }
  return params;
};

export const downloadFile = url => {
  // eslint-disable-next-line security/detect-non-literal-fs-filename
  window.open(url);
};

export const objectToArrayByCompare = (target, key, param) => {
  const result = [];
  if (!key || !param) {
    result.push(target);
  }
  if (key && param && lodash.has(target, key) && target[key] === param) {
    result.push(target);
  }
  return result;
};

/*
 * Download file
 *
 * Parameters:
 *   uri - Contain the http response.
 *   filename - Specify the filename (optional).
 *   contentType - content type (optional)
 *   token - needed for older browser e.g. IE9 or below
 */
export const saveFile = (uri, filename, contentType, token) => {
  if (!uri) {
    return;
  }

  const name = filename || uri.header["content-disposition"].split(";")[1].split("=")[1];

  if (Blob) {
    const blob = new Blob([uri.xhr.response], {
      type: contentType || uri.type,
    });

    FileSaver.saveAs(blob, name);
  } else {
    //fall back to IE9 and below
    // eslint-disable-next-line security/detect-non-literal-fs-filename
    window.open(`${uri.xhr.responseURL}?sessionToken=${encodeURIComponent(token)}`);
  }
};

export const parseByString = (path, obj) =>
  path.split(".").reduce((prev, curr) => (prev ? prev[curr] : undefined), obj || self);

export const getFileProps = file => {
  return !file
    ? null
    : file.pkDigitalContent
    ? {
        ...file,
        src: file.downloadUrl,
        type: file.contentType,
        name: isExist(file.fileName)
          ? file.fileName
          : `${file.service}-${file.field}-${file.pkDigitalContent}`,
        isLocalFile: false,
      }
    : file.name //local file
    ? {
        type: file.type,
        name: file.name,
        src: file.preview,
        size: file.size,
        isLocalFile: true,
      }
    : file;
};

export const getFilePropsPP = file => {
  return file.objectId > 0
    ? {
        name: file.fileName,
        fileUrl: file.fileUrl,
        contentType: file.contentType,
        isLocalFile: false,
      }
    : {
        contentType: file.type || file.file.type,
        name: file.name || file.file.name,
        fileUrl: file.preview,
        size: file.size || file.file.size,
        isLocalFile: true,
      };
};

// const get = (path, obj, fb = `$\{${path}}`) =>
//   path.split(".").reduce((res, key) => res[key] || fb, obj);

const get = (path, obj, fb = `$\{${path}}`) =>
  path.split(".").reduce((res, key) => (lodash.isUndefined(res[key]) ? fb : res[key]), obj);

const parseFunction = (fnTemplate, templateFnMap) => {
  const fnWithParamsMatch = /'@(.+)\((.+)\)'/g.exec(fnTemplate);
  if (fnWithParamsMatch) {
    const [, templateFn, paramStr] = fnWithParamsMatch;
    const params = paramStr.replace(" ", "").split(",");
    return {
      fn: templateFnMap[templateFn],
      params,
    };
  }

  const paramMatch = /'@(.+)'/g.exec(fnTemplate);
  if (paramMatch) {
    const [, param] = paramMatch;
    return {
      param,
    };
  }
  return fnTemplate;
};

export const parseTemplate = (templateStr, templateFnMap) => {
  const splits = templateStr.split(/(\$\{.+?})/);
  const parsedSplit = splits.map(fragment => {
    if (!fragment.startsWith("$")) return fragment;
    const match = /\$\{(.+?)}/g.exec(fragment);
    const [, fnTemplate] = match;
    return parseFunction(fnTemplate.trim(), templateFnMap);
  });

  return map =>
    parsedSplit.reduce((finalStr, fragment, i) => {
      let finalNode;
      if (lodash.isString(fragment)) {
        finalNode = fragment;
      } else {
        const { fn, param, params } = fragment;
        const paramValue = params ? params.map(x => get(x, map)) : get(param, map);
        const comp = fn ? fn(...paramValue) : paramValue;

        finalNode = React.isValidElement(comp) ? React.cloneElement(comp, { key: i }) : comp;
      }
      return [...finalStr, ...(finalNode ? [finalNode] : [])];
    }, []);
};

export const smallizeFirstLetter = string => string.charAt(0).toUpperCase() + string.slice(1);

export const getLocalizedImageUrl = (baseUrl, selectedLanguage) => {
  if (selectedLanguage && selectedLanguage.code) {
    const languageValueArray = selectedLanguage.code.split("-");
    return `${baseUrl}_${languageValueArray[0]}_${languageValueArray[1].toUpperCase()}.png`;
  }
  return `${baseUrl}_en_US.png`;
};

export const clearLocalStorage = () => {
  // skip loginMode, only remove when upgrade user session is called
  const __skipKeys = ["selectedLanguage", "loginMode", "hideMessageBoard"];

  for (const key in localStorage) {
    if (!__skipKeys.includes(key)) {
      localStorage.removeItem(key);
    }
  }

  // SP initiated login.  This prevents the DefaultLoginPage from picking up the previous
  // returnUrl if the user logs out from cxaone.
  sessionStorage.clear();
};

export const getUniqueIdCombination = row =>
  Object.keys(row)
    .filter(k => k.toLowerCase().endsWith("id"))
    .map(k => row[k])
    .join();

// HDF Render value
// export const renderValue = (data, defaultValue, employeeParticulars) => {
//   const { fieldName, fieldFormat, dateFormat } = data;
//
//   const employeeDataObj = employeeParticulars.byId;
//
//   if (isEmpty(employeeDataObj)) {
//     return defaultValue;
//   }
//
//   if (fieldName in employeeDataObj && !employeeDataObj[fieldName].fieldValue) {
//     return " ";
//   }
//
//   if (fieldName in employeeDataObj && fieldFormat === "date") {
//     const setDateFormat = dateFormat || "";
//     return moment(employeeDataObj[fieldName].fieldValue).format(setDateFormat);
//   }
//
//   if (fieldName in employeeDataObj) {
//     return employeeDataObj[fieldName].fieldValue;
//   }
//
//   return null;
// };

export const getUrlParameters = url => {
  const params = new URLSearchParams(new URL(url).search);
  const queryParams = {};
  params.forEach((value, key) => {
    queryParams[key] = value;
  });
  return queryParams;
};

/**
 * @TODO diet bundle size
 */

export const getClientConfig = client => CLIENT_CONFIG[client] || CLIENT_CONFIG["cxa"];

accounting.settings.currency.format = {
  pos: "%s%v",
  neg: "%s-%v",
  zero: "%s%v",
};

accounting.setCurrencySettings = (settings = {}) =>
  (accounting.settings.currency = {
    ...accounting.settings.currency,
    ...settings,
  });

accounting.setNumberSettings = (settings = {}) =>
  (accounting.settings.number = {
    ...accounting.settings.number,
    ...settings,
  });

accounting.getPostfixCurrencyFormat = () => ({
  ...accounting.settings.currency.format,
  pos: "%v %s",
  neg: "-%v %s",
  zero: "%v %s",
});

/**
 * Get first element of array
 * @param  {array} items
 * @return {*}
 */
export const getFirst = (items = []) => [...items].shift();

/**
 * Get last element of array
 * @param  {array} items
 * @return {*}
 */
export const getLast = (items = []) => [...items].pop();

export const insertSpaceBeforeUpperCase = str => str.split(/(?=[A-Z])/).join(" ");

export const getPropsBySelector = selector => state => (...keys) => {
  return keys.reduce(
    (acc, key) => ({
      ...acc,
      [key]: selector(state, key),
    }),
    {}
  );
};

function _getDateRangeFromBenefitPlan(by = "day") {
  return ({ planStart, planEnd }) => {
    const startDate = moment(planStart).startOf(by);
    const endDate = moment(planEnd).endOf(by);

    return { startDate, endDate };
  };
}

const getDateRangeFromBenefitPlan = _getDateRangeFromBenefitPlan();
getDateRangeFromBenefitPlan.withBy = _getDateRangeFromBenefitPlan;

export const getToday = (by = "day") => moment().startOf(by);

export const range = (begin, end, interval = 1) => {
  let arr = [];

  for (let i = begin; i < end; i += interval) {
    arr = [...arr, i];
  }

  return arr;
};

export const getDomainName = url => {
  if (!url) {
    url = location.hostname;
  }
  const idx = url.indexOf(".");
  if (idx > 0) {
    return url.substr(idx);
  }
  return url;
};

export const getQueryParamFromLocation = param => {
  const urlParams = new URLSearchParams(location.search);
  return urlParams.get(param);
};

export * from "./is";
export { default as getUrlSeparator } from "./getUrlSeparator";
export { default as serializeQuery } from "./serializeQuery";
export { default as deserializeQuery } from "./deserializeQuery";
export { default as removeParams } from "./removeParams";
export { default as getUrlWithQuery } from "./getUrlWithQuery";
export { default as getStyle } from "./getStyle";
export { default as scrollTo } from "./scrollTo";
export { default as isOutOfViewport } from "./isOutOfViewport";
export { compose, getDateRangeFromBenefitPlan, benefitsErrorHandler };
export { renderAsHTML, decodeHtmlContent } from "./html.js";



// WEBPACK FOOTER //
// ./utils/index.js